// Gestionnaire de session pour la persistance et reconnexion
import { v4 as uuidv4 } from 'uuid'

export interface SessionData {
  playerId: string
  playerName?: string
  playerTheme?: string
  currentRoomId?: string
  joinedAt?: number
}

const SESSION_STORAGE_KEY = 'reminiscence_session'

export class SessionManager {
  private static instance: SessionManager
  private sessionData: SessionData

  private constructor() {
    this.sessionData = this.loadSession()
  }

  public static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager()
    }
    return SessionManager.instance
  }

  // Génère ou récupère le playerId unique (Ticket de Vestiaire)
  public getPlayerId(): string {
    if (!this.sessionData.playerId) {
      this.sessionData.playerId = uuidv4()
      this.saveSession()
      console.log('🎫 Nouveau playerId généré:', this.sessionData.playerId)
    }
    return this.sessionData.playerId
  }

  // Stocke les informations de la partie en cours
  public startGame(roomId: string, playerName: string, playerTheme: string): void {
    this.sessionData.currentRoomId = roomId
    this.sessionData.playerName = playerName
    this.sessionData.playerTheme = playerTheme
    this.sessionData.joinedAt = Date.now()
    this.saveSession()
    console.log('🎮 Session de jeu démarrée:', { roomId, playerName, playerTheme })
  }

  // Termine la partie (victoire, défaite, abandon)
  public endGame(): void {
    this.sessionData.currentRoomId = undefined
    this.sessionData.playerName = undefined
    this.sessionData.playerTheme = undefined
    this.sessionData.joinedAt = undefined
    this.saveSession()
    console.log('🏁 Session de jeu terminée')
  }

  // Vérifie s'il y a une partie en cours à récupérer
  public hasActiveGame(): boolean {
    return !!(this.sessionData.currentRoomId && this.sessionData.joinedAt)
  }

  // Récupère les données de reconnexion
  public getReconnectionData(): { playerId: string, roomId: string, playerName: string, playerTheme: string } | null {
    if (!this.hasActiveGame()) {
      return null
    }

    return {
      playerId: this.sessionData.playerId,
      roomId: this.sessionData.currentRoomId!,
      playerName: this.sessionData.playerName!,
      playerTheme: this.sessionData.playerTheme!
    }
  }

  // Sauvegarde dans localStorage
  private saveSession(): void {
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(this.sessionData))
    } catch (error) {
      console.error('❌ Erreur sauvegarde session:', error)
    }
  }

  // Charge depuis localStorage
  private loadSession(): SessionData {
    try {
      // PATCH DEBUG: Permettre de forcer l'ID via l'URL pour les tests
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search)
        const forcedId = urlParams.get('forcePlayerId')
        if (forcedId) {
          console.log('🔧 Session forced via URL:', forcedId)
          return { playerId: forcedId }
        }
      }

      const stored = localStorage.getItem(SESSION_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        console.log('📂 Session récupérée:', parsed)
        return parsed
      }
    } catch (error) {
      console.error('❌ Erreur chargement session:', error)
    }

    // Session par défaut
    return {
      playerId: uuidv4()
    }
  }

  // Nettoie complètement la session (reset total)
  public clearSession(): void {
    this.sessionData = { playerId: uuidv4() }
    this.saveSession()
    console.log('🧹 Session nettoyée')
  }

  // Getters pour accès aux données
  public getCurrentRoomId(): string | undefined {
    return this.sessionData.currentRoomId
  }

  public getPlayerName(): string | undefined {
    return this.sessionData.playerName
  }

  public getPlayerTheme(): string | undefined {
    return this.sessionData.playerTheme
  }
}

// Export de l'instance singleton
export const sessionManager = SessionManager.getInstance()
